import {
  FeedbackApiClient,
  type CreateRecordingUploadInput,
  type CreateRecordingUploadResult,
  type SubmitFeedbackResult,
} from '@/clients/feedbackApiClient';
import { getSessionId, getRecentCorrelationIds } from '@/lib/correlationStore';
import type { Bundle } from '@financialharmony/show-and-tell';

export interface FinalizeRecordingParams {
  recordingId: string;
  clientSubmissionId: string;
  message: string;
  route?: string;
  context: Record<string, string>;
  sentryEventId?: string;
  sentryReplayId?: string;
  bundle: Bundle;
}

export interface SubmitFeedbackParams {
  message: string;
  clientSubmissionId: string;
  route?: string;
  context: Record<string, string>;
  sentryEventId?: string;
  sentryReplayId?: string;
}

/**
 * FIN-869 (in-app-feedback stack PR6): consultant-app counterpart of apps/client's
 * lib/feedback/actions.ts. `app` is always `'consultant'` — a platform admin submits through
 * this same path (submit_feedback_atomic derives role/is_internal server-side; see
 * FeedbackPanel.tsx for the EC8/EC19 chip and tagging behaviour this enables).
 */
export class FeedbackService {
  private apiClient: FeedbackApiClient;

  constructor() {
    this.apiClient = new FeedbackApiClient();
  }

  async submitFeedback(params: SubmitFeedbackParams): Promise<SubmitFeedbackResult> {
    return this.apiClient.submitFeedback({
      message: params.message,
      clientSubmissionId: params.clientSubmissionId,
      app: 'consultant',
      route: params.route,
      context: params.context,
      sessionId: getSessionId(),
      recentCorrelationIds: getRecentCorrelationIds(),
      sentryEventId: params.sentryEventId,
      sentryReplayId: params.sentryReplayId,
    });
  }

  async createRecordingUpload(params: CreateRecordingUploadInput): Promise<CreateRecordingUploadResult> {
    return this.apiClient.createRecordingUpload(params);
  }

  async finalizeRecording(params: FinalizeRecordingParams): Promise<SubmitFeedbackResult> {
    return this.apiClient.finalizeRecording({
      recordingId: params.recordingId,
      clientSubmissionId: params.clientSubmissionId,
      app: 'consultant',
      message: params.message,
      route: params.route,
      context: params.context,
      sessionId: getSessionId(),
      recentCorrelationIds: getRecentCorrelationIds(),
      sentryEventId: params.sentryEventId,
      sentryReplayId: params.sentryReplayId,
      bundle: params.bundle,
    });
  }
}
